import { create } from 'zustand';
import { CapabilityType, CapabilityPlacement, PlacementType } from '../types/wargame-capabilities';
import { TeamColor } from '../types/wargame-teams';
import { CAPABILITY_DEFINITIONS } from '../utils/capabilityEffects';

const generateId = () => Math.random().toString(36).substring(2, 9);

interface CapabilityStore {
  capabilitySlots: number;
  maxCapabilitySlots: number;
  placedCapabilities: CapabilityPlacement[];
  
  initializeSlots: (stage: 1 | 2 | 3) => void;
  addCapabilitySlot: () => void;
  consumeSlots: (amount: number) => boolean;
  restoreSlots: (amount: number) => void;
  
  placeCapability: (
    type: CapabilityType, 
    locationId: string, 
    placementType: PlacementType,
    teamId: TeamColor,
    turn: number
  ) => boolean;
  
  removeCapability: (capabilityId: string) => void;
  getCapabilitiesAt: (locationId: string) => CapabilityPlacement[];
  reset: () => void;
}

export const useCapabilityStore = create<CapabilityStore>((set, get) => ({
  capabilitySlots: 20, // Default for Stage 1
  maxCapabilitySlots: 20,
  placedCapabilities: [],

  initializeSlots: (stage) => {
    const slots = stage === 1 ? 20 : stage === 2 ? 25 : 30;
    set({ capabilitySlots: slots, maxCapabilitySlots: slots, placedCapabilities: [] });
  },

  addCapabilitySlot: () => {
    set(state => ({ 
      capabilitySlots: state.capabilitySlots + 1,
      maxCapabilitySlots: state.maxCapabilitySlots + 1 
    }));
  },

  consumeSlots: (amount) => {
    const { capabilitySlots } = get();
    if (capabilitySlots < amount) return false;
    set({ capabilitySlots: capabilitySlots - amount });
    return true;
  },

  restoreSlots: (amount) => {
    set(state => ({ capabilitySlots: state.capabilitySlots + amount }));
  },

  placeCapability: (type, locationId, placementType, teamId, turn) => {
    const { consumeSlots, placedCapabilities } = get();
    const definition = CAPABILITY_DEFINITIONS[type];
    
    if (!definition) return false;
    if (!consumeSlots(definition.cost)) return false;

    const newPlacement: CapabilityPlacement = {
      capabilityId: generateId(),
      type,
      placedAt: locationId,
      teamId,
      turn,
      effects: definition.effects
    };

    const isInstant = definition.effects.some(e => e.includes('Instantaneous'));
    
    if (!isInstant) {
      set({ placedCapabilities: [...placedCapabilities, newPlacement] });
    }

    return true;
  },

  removeCapability: (capabilityId) => {
    const { placedCapabilities, restoreSlots } = get();
    const cap = placedCapabilities.find(c => c.capabilityId === capabilityId);
    if (!cap) return;

    const definition = CAPABILITY_DEFINITIONS[cap.type];
    if (definition) {
      restoreSlots(definition.cost);
    }

    set({ placedCapabilities: placedCapabilities.filter(c => c.capabilityId !== capabilityId) });
  },

  getCapabilitiesAt: (locationId) => {
    return get().placedCapabilities.filter(c => c.placedAt === locationId);
  },

  reset: () => set({ capabilitySlots: 20, maxCapabilitySlots: 20, placedCapabilities: [] })
}));
